import React from 'react';
import { Seo, breadcrumbJsonLd } from '@/components/Seo';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { StudioSlideshow } from '@/components/StudioSlideshow';
import { SITE, PHOTOS, STUDIO_GALLERY } from '@/data/site-config';
import { MapPin, Phone } from 'lucide-react';

export default function FacilityPage() {
  const path = '/facility';
  const breadcrumbs = [{ name: 'Home', path: '/' }, { name: 'Facility', path }];
  return (
    <>
      <Seo
        title="Our Studio & Facility"
        description={`Tour the private Fitness Training 360 studio at ${SITE.address.full}.`}
        canonical={path}
        image={STUDIO_GALLERY[0]?.src || PHOTOS.jaradWithStaffStorefront}
        jsonLd={breadcrumbJsonLd(breadcrumbs)}
      />
      <Breadcrumbs items={breadcrumbs} />
      <section className="container-x py-10">
        <span className="eyebrow">Our private studio</span>
        <h1 className="font-display text-4xl md:text-5xl font-bold mt-3" data-testid="facility-page-h1">
          A focused, private place to train.
        </h1>
        <p className="text-[color:var(--ft-muted)] text-lg mt-4 max-w-3xl">
          The studio at {SITE.address.line1} in {SITE.city} is private, appointment-only, and built around 1-on-1 and semi-private training — no crowds, no fighting for equipment.
        </p>

        {/* Slideshow — hero of the page */}
        <div className="mt-8">
          <StudioSlideshow images={STUDIO_GALLERY} testId="facility-slideshow" />
        </div>

        {/* Full gallery grid — all photos visible at once for scanability */}
        <div className="mt-12">
          <h2 className="font-display text-2xl md:text-3xl font-bold" data-testid="facility-gallery-h2">
            Studio gallery.
          </h2>
          <p className="text-[color:var(--ft-muted)] mt-2 max-w-2xl">
            Every photo below is from inside the actual {SITE.name} studio in {SITE.city}.
          </p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-6">
            {STUDIO_GALLERY.map((img, i) => (
              <img
                key={img.src}
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="surface w-full h-72 sm:h-80 object-cover"
                data-testid={`facility-gallery-${i}`}
              />
            ))}
          </div>
        </div>

        {/* Contact / hours */}
        <div className="grid gap-4 sm:grid-cols-3 mt-12">
          <div className="surface p-5">
            <MapPin size={18} className="text-accent" />
            <h2 className="font-display font-semibold mt-3">Address</h2>
            <p className="text-[color:var(--ft-muted)] mt-1">{SITE.address.line1}<br />{SITE.address.line2}</p>
            <p className="text-xs text-[color:var(--ft-dim)] mt-2">{SITE.address.note}</p>
          </div>
          <div className="surface p-5">
            <Phone size={18} className="text-accent" />
            <h2 className="font-display font-semibold mt-3">Contact</h2>
            <p className="text-[color:var(--ft-muted)] mt-1">
              <a className="hover:text-[color:var(--ft-text)]" href={`tel:${SITE.phoneE164}`}>{SITE.phone}</a>
            </p>
            <p className="text-[color:var(--ft-muted)]">
              <a className="hover:text-[color:var(--ft-text)] break-all" href={`mailto:${SITE.email}`}>{SITE.email}</a>
            </p>
            <p className="text-[color:var(--ft-muted)] mt-2">
              <a className="hover:text-[color:var(--ft-text)]" target="_blank" rel="noreferrer" href={SITE.booking.url}>Book on Vagaro &rarr;</a>
            </p>
          </div>
          <div className="surface p-5">
            <h2 className="font-display font-semibold">By appointment only</h2>
            <p className="text-[color:var(--ft-muted)] mt-1 text-sm">
              Sessions are scheduled in advance. Available {SITE.hoursDetail.toLowerCase()}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
